import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import {
  DOMParser as ProseMirrorDOMParser,
  Fragment,
  Slice,
  type Node as PMNode,
} from "@tiptap/pm/model";

import { escapeXml, parseTagString, regexToHTML, serializeToMarkdown, unescapeXml } from "../utils";

import {
  convertWorkspaceUrlsToTags,
  convertWorkspaceUrlsToTagsAsync,
} from "@/utils/workspaceUrlTags";

/**
 * Inserts text with proper paragraph structure for newlines.
 * This ensures that pasted text with line breaks creates proper paragraph nodes
 * instead of inserting raw newline characters.
 */
function insertTextWithParagraphs(
  view: Parameters<NonNullable<Plugin["props"]["handlePaste"]>>[0],
  text: string,
) {
  const { state, dispatch } = view;
  const { schema, tr } = state;

  // Split text by newlines (handle both \r\n and \n)
  const lines = text.split(/\r?\n/);

  // If there's only one line (no newlines), just insert as text
  if (lines.length === 1) {
    dispatch(tr.insertText(text));
    return;
  }

  // Create paragraph nodes for each line
  const paragraphType = schema.nodes.paragraph;

  if (!paragraphType) {
    // Fallback to simple text insertion if paragraph type not available
    dispatch(tr.insertText(text));
    return;
  }

  // Build an array of paragraph nodes
  const paragraphs = lines.map(line => {
    if (line === "") {
      // Empty line becomes empty paragraph
      return paragraphType.create();
    }
    // Non-empty line becomes paragraph with text content
    return paragraphType.create(null, schema.text(line));
  });

  // Create a fragment from the paragraphs
  const fragment = Fragment.from(paragraphs);

  // Create a slice to insert
  // openStart and openEnd of 1 means we're inserting block content that should merge
  // with surrounding paragraphs appropriately
  const slice = new Slice(fragment, 1, 1);

  // Replace the current selection with the new content
  dispatch(tr.replaceSelection(slice));
}

const TAG_PATTERN = /@@@tag:(.*?)@@@/g;

type TagTokenFragment = { type: "text"; value: string } | { type: "tag"; value: string };

const parseTagTokenFragments = (text: string): TagTokenFragment[] => {
  const fragments: TagTokenFragment[] = [];
  let cursor = 0;

  TAG_PATTERN.lastIndex = 0;
  for (const match of text.matchAll(TAG_PATTERN)) {
    const start = match.index ?? 0;
    const rawTag = match[0];

    if (start > cursor) {
      fragments.push({ type: "text", value: text.slice(cursor, start) });
    }

    fragments.push({ type: "tag", value: match[1] ?? "" });
    cursor = start + rawTag.length;
  }

  if (cursor < text.length) {
    fragments.push({ type: "text", value: text.slice(cursor) });
  }

  return fragments;
};

const hasTagToken = (text: string): boolean => {
  TAG_PATTERN.lastIndex = 0;
  const hasMatch = TAG_PATTERN.test(text);
  TAG_PATTERN.lastIndex = 0;
  return hasMatch;
};

const insertTagTokenContent = (
  view: Parameters<NonNullable<Plugin["props"]["handlePaste"]>>[0],
  text: string,
): boolean => {
  const { state, dispatch } = view;
  const { schema, tr } = state;
  const tagMentionType = schema.nodes.tagMention;
  if (!tagMentionType) {
    return false;
  }

  const nodes = parseTagTokenFragments(text).flatMap(fragment => {
    if (fragment.type === "text") {
      return fragment.value ? [schema.text(fragment.value)] : [];
    }

    return [tagMentionType.create(parseTagString(fragment.value))];
  });

  if (!nodes.some(node => node.type === tagMentionType)) {
    return false;
  }

  dispatch(tr.replaceSelection(new Slice(Fragment.from(nodes), 0, 0)));
  return true;
};

function buildInlineFriendlyPasteSlice(parsedRoot: PMNode): Slice | null {
  const blocks: PMNode[] = [];
  for (let i = 0; i < parsedRoot.childCount; i++) {
    blocks.push(parsedRoot.child(i));
  }

  // Extra `<p></p>` from blank lines / trailing newlines in clipboard → multi-block
  // slice and a new paragraph. Drop empty paragraphs first.
  const meaningful = blocks.filter(
    block => !(block.type.name === "paragraph" && block.content.size === 0),
  );
  if (meaningful.length === 0) {
    return null;
  }

  if (meaningful.length === 1) {
    const only = meaningful[0];
    if (only.type.name === "paragraph") {
      return new Slice(only.content, 1, 1);
    }
    return Slice.maxOpen(Fragment.from([only]));
  }

  return new Slice(Fragment.from(meaningful), 1, 1);
}

function insertParsedContent(
  view: Parameters<NonNullable<Plugin["props"]["handlePaste"]>>[0],
  text: string,
) {
  const { state, dispatch } = view;
  const { schema, tr } = state;

  const normalizedText = text.replace(/\r\n/g, "\n").replace(/^\n+|\n+$/g, "");
  let htmlContent = regexToHTML(escapeXml(normalizedText));
  htmlContent = htmlContent.replace(/^(<p><\/p>)+/gi, "").replace(/(<p><\/p>)+$/gi, "");
  if (!htmlContent.trim()) {
    return;
  }

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlContent;

  const parser = ProseMirrorDOMParser.fromSchema(schema);
  const parsedRoot = parser.parse(tempDiv);
  const slice = buildInlineFriendlyPasteSlice(parsedRoot);

  if (!slice || !slice.content.size) {
    return;
  }

  dispatch(tr.replaceSelection(slice));
}

export const ClipboardHandler = Extension.create({
  name: "clipboardHandler",

  addOptions() {
    return {
      mode: "f-string" as "f-string" | "jinja2",
      userToken: null as string | null,
    };
  },

  addProseMirrorPlugins() {
    const { userToken } = this.options;

    return [
      new Plugin({
        key: new PluginKey("clipboardHandler"),
        props: {
          handlePaste: (view, event) => {
            // Get HTML and plain text from clipboard
            const html = event.clipboardData?.getData("text/html");
            const text = event.clipboardData?.getData("text/plain");

            if (text && view.state.schema.nodes.tagMention) {
              const converted = convertWorkspaceUrlsToTags(text);
              if (converted !== text) {
                event.preventDefault();
                convertWorkspaceUrlsToTagsAsync(text, { userToken })
                  .then(resolvedText => {
                    if (!insertTagTokenContent(view, resolvedText)) {
                      insertParsedContent(view, resolvedText);
                    }
                  })
                  .catch(() => undefined);
                return true;
              }
            }

            if (text && hasTagToken(text)) {
              event.preventDefault();
              if (!insertTagTokenContent(view, text)) {
                insertParsedContent(view, text);
              }
              return true;
            }

            // Check if HTML looks like VSCode or Slack copied content
            const isVSCodeOrSlackHTML =
              html &&
              // VSCode indicators
              (html.includes("monaco-editor") ||
                html.includes("view-line") ||
                html.includes("mtk") ||
                html.includes("data-vscode") ||
                // Slack indicators
                html.includes("slack-") ||
                html.includes("c-message") ||
                html.includes("ql-editor") ||
                html.includes("slack_kit_") ||
                // General code editor indicators
                html.includes("hljs-") ||
                html.includes("language-") ||
                html.includes("code-block") ||
                html.includes("syntax-") ||
                // Check for excessive inline styles (common in these copies)
                (html.match(/style="[^"]*"/g) || []).length > 10);

            if (isVSCodeOrSlackHTML && text) {
              // eslint-disable-next-line no-console
              console.warn("Dirty paste detected, inserting as plain text");
              event.preventDefault();
              insertTextWithParagraphs(view, text);
              return true;
            }

            // For plain text pastes (no HTML), handle newlines properly
            // This catches cases where the clipboard only has plain text
            if (!html && text && text.includes("\n")) {
              event.preventDefault();
              insertTextWithParagraphs(view, text);
              return true;
            }

            return false;
          },

          clipboardTextSerializer: (slice: Slice, view) => {
            const tempDoc = view.state.doc.copy(slice.content);

            const tempEditor = {
              state: {
                doc: tempDoc,
              },
            };

            const serializedText = unescapeXml(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              serializeToMarkdown(tempEditor as any),
            );

            return serializedText;
          },
        },
      }),
    ];
  },
});
